/**
 * Cross-platform call notification system
 * Works on web (Notification API + vibration), iOS (CallKit), and Android (ConnectionService)
 * Handles alerting even when app is in background
 */

import { Capacitor } from '@capacitor/core';

let notificationPermission: NotificationPermission = 'default';
let vibrationInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Request notification permission (call early in app lifecycle)
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    // Native platforms use CallKit/ConnectionService - permission handled natively
    return true;
  }

  if (!('Notification' in window)) return false;

  if (Notification.permission === 'granted') {
    notificationPermission = 'granted';
    return true;
  }

  if (Notification.permission !== 'denied') {
    const result = await Notification.requestPermission();
    notificationPermission = result;
    return result === 'granted';
  }

  return false;
}

/**
 * Show an incoming call notification (works in background on web)
 * Lazily requests notification permission if not yet granted.
 */
export function showIncomingCallNotification(
  callerName: string,
  isVideo: boolean,
  onAnswer?: () => void,
  onReject?: () => void
): { dismiss: () => void } {
  // Start vibration pattern (pulse)
  startVibrationPattern();

  // Lazily request notification permission on first incoming call
  if (!Capacitor.isNativePlatform() && 'Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then((result) => {
      notificationPermission = result;
      if (result === 'granted') {
        // Permission just granted - show notification now
        showWebNotification(callerName, isVideo, onAnswer);
      }
    });
  }

  if (Capacitor.isNativePlatform()) {
    // Native: CallKit/ConnectionService handles the UI
    return { dismiss: stopVibrationPattern };
  }

  // Web: use Notification API for background alerting
  const notifHandle = showWebNotification(callerName, isVideo, onAnswer);

  return {
    dismiss: () => {
      notifHandle?.close();
      stopVibrationPattern();
    },
  };
}

/**
 * Internal: create a web Notification instance
 */
function showWebNotification(
  callerName: string,
  isVideo: boolean,
  onAnswer?: () => void,
): Notification | null {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return null;
  }

  const callType = isVideo ? '📹 Video Call' : '📞 Voice Call';

  const notifOptions: NotificationOptions & Record<string, unknown> = {
    body: `Incoming ${callType} • elefa Connect`,
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'incoming-call',
    requireInteraction: true,
    silent: false,
  };

  const notification = new Notification(`${callerName}`, notifOptions);

  notification.onclick = () => {
    window.focus();
    onAnswer?.();
    notification.close();
  };

  return notification;
}

/**
 * Show a missed call notification
 */
export function showMissedCallNotification(callerName: string, isVideo: boolean) {
  if (Capacitor.isNativePlatform()) return; // Native handles via system call log

  if ('Notification' in window && Notification.permission === 'granted') {
    const callType = isVideo ? 'video' : 'voice';
    new Notification(`Missed ${callType} call`, {
      body: `${callerName} tried to reach you on elefa Connect`,
      icon: '/favicon.ico',
      tag: 'missed-call',
      requireInteraction: false,
    });
  }
}

/**
 * Start haptic vibration pattern for incoming calls
 */
function startVibrationPattern() {
  if (!('vibrate' in navigator)) return;

  // Initial vibration
  navigator.vibrate([200, 100, 200, 100, 200]);

  // Repeat every 2 seconds
  vibrationInterval = setInterval(() => {
    navigator.vibrate([200, 100, 200, 100, 200]);
  }, 2000);
}

/**
 * Stop vibration
 */
function stopVibrationPattern() {
  if (vibrationInterval) {
    clearInterval(vibrationInterval);
    vibrationInterval = null;
  }
  if ('vibrate' in navigator) {
    navigator.vibrate(0);
  }
}
